import { type ReactNode } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import FullScreenComponent from "./app/main/Fullscreen";
import AuthFlowContainer from "./app/user/AuthFlowContainer";
import RegisterFlowContainer from "./app/user/RegisterFlowContainer";
import NewTicket from "./app/public/NewTicket";
import PublicForm from "./app/public/PublicForm";
import PublicFormSubmissionDone from "./app/public/PublicFormSubmissionDone";
import PublicFormsLanding from "./app/public/PublicFormsLanding";
import PublicFormSubmissionView from "./app/public/PublicFormSubmissionView";
import InfoPage from "./app/public/InfoPage";
import EtsgPrivacyPage from "./app/public/EtsgPrivacyPage";
import PrivacyPolicyPage from "./app/public/PrivacyPolicyPage";
import Registration from "./app/user/Registration";
import RegisterComplete from "./app/user/RegisterComplete";
import LoginVerify from "./app/user/LoginVerify";
import Components from "./app/admin/Components";
import PermissionManager from "./app/admin/acl/PermissionManager";
import PublicFormsManager from "./app/admin/forms/PublicFormsManager";
import PublicJsonFeedsManager from "./app/admin/forms/PublicJsonFeedsManager";
import EmailAdminHub from "./app/admin/email/EmailAdminHub";
import EmailProviderPanel from "./app/admin/email/EmailProviderPanel";
import EmailQuotaPanel from "./app/admin/email/EmailQuotaPanel";
import EmailLogsPanel from "./app/admin/logs/EmailLogsPanel";
import EmailTestPanel from "./app/admin/logs/EmailTestPanel";
import AdminUsersPage from "./app/admin/AdminUsersPage";
import AdminDashboard from "./app/admin/AdminDashboard";
import { ConnectorsPage } from "./app/admin/ConnectorsPage";
import { ConnectorFilesPage } from "./app/admin/ConnectorFilesPage";
import AdminGroupsPage from "./app/admin/AdminGroupsPage";
import TenantsPage from "./app/admin/TenantsPage";
import MongoCollectionsInspector from "./app/admin/MongoCollectionsInspector";
import Actions from "./app/actions/Actions";
import { PrivateRoute } from "./app/auth/PrivateRoute";
import { AdminGuard } from "./app/auth/AdminGuard";
import { FormConfigGuard } from "./app/auth/FormConfigGuard";
import { AdminComponentsGuard } from "./app/auth/AdminComponentsGuard";
import { AuthProvider, useAuth } from "./app/auth/AuthContext";
import { isUiSectionHidden, type UiSettingsFlag } from "./app/user/uiSettings";
import { ACLProvider } from "./app/acl";
import StatePage from "./app/system/StatePage";
import ComponentTable from "./app/component/ComponentTable";
import { ErrorBoundary } from "react-error-boundary";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Home from "./app/user/Home";
import InboxPage from "./app/inbox/InboxPage";
import ProfilePage from "./app/user/ProfilePage";
import CollectionsPage from "./app/collections/CollectionsPage";
import FilesPage from "./app/files/FilesPage";
import Login from "./app/user/Login";
import NotFoundPage from "./app/system/NotFoundPage";
import { isOrganizationScopingDisabled } from "./app/utils/organizationScoping";
import { buildCollectionPath } from "./app/utils/collectionPaths";

function RedirectIfUiHidden({
  flag,
  children,
}: {
  flag: UiSettingsFlag;
  children: ReactNode;
}) {
  const { user } = useAuth();
  if (isUiSectionHidden(user, flag)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function LegacyCollectionPathRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const s = typeof slug === "string" ? slug.replace(/[!]+$/, "").trim() : "";
  if (!s) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/${s}`} replace />;
}

/** While org scoping is off, canonical collection URLs are /:slug only (see issue #16). */
function OrgPrefixedCollectionRedirect() {
  const { org, slug } = useParams<{ org?: string; slug?: string }>();
  const { user, loading } = useAuth();
  if (org === "my-submissions") {
    if (loading) return null;
    if (isUiSectionHidden(user, "hideSubmissions")) {
      return <Navigate to="/" replace />;
    }
  }
  const s = typeof slug === "string" ? slug.replace(/[!]+$/, "").trim() : "";
  if (!s) {
    return <Navigate to="/" replace />;
  }
  if (isOrganizationScopingDisabled()) {
    return <Navigate to={buildCollectionPath({ slug: s })} replace />;
  }
  return (
    <PrivateRoute requireAdmin={false} logClient404WhenUnauthenticated>
      <FullScreenComponent>
        <ComponentTable />
      </FullScreenComponent>
    </PrivateRoute>
  );
}

const App = () => {
  const Fallback = ({ error, resetErrorBoundary }: { error: any, resetErrorBoundary: any }) => {
    return (
      <div className="w-[500px]">
        <p>Something went wrong:</p>
        <pre style={{ color: "red" }}>{error.message}</pre>
        <button onClick={resetErrorBoundary}>Try again</button>
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackRender={({ error, resetErrorBoundary }) => Fallback({ error, resetErrorBoundary })}>
      <AuthProvider>
        <ACLProvider>
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              <Route path="/" element={
                <PrivateRoute requireAdmin={false} children={
                  <FullScreenComponent
                    title={"Home"}
                    children={<Home />}
                  />
                }
                />
              } />
              <Route path="/login" element={<AuthFlowContainer />} />
              <Route path="/login/:tenantAbbreviation" element={<AuthFlowContainer />} />
              <Route path="/login/verify" element={<LoginVerify />} />
              <Route path="/ticket" element={<NewTicket />} />
              <Route path="/forms" element={<PublicFormsLanding />} />
              <Route path="/forms/:token/submitted" element={<PublicFormSubmissionDone />} />
              <Route path="/forms/:token" element={<PublicForm />} />
              <Route path="/public" element={<PublicFormsLanding />} />
              <Route path="/public/forms/:token/submitted" element={<PublicFormSubmissionDone />} />
              <Route path="/public/forms/:token" element={<PublicForm />} />
              <Route
                path="/my-submissions"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <RedirectIfUiHidden flag="hideSubmissions">
                      <FullScreenComponent>
                        <ComponentTable />
                      </FullScreenComponent>
                    </RedirectIfUiHidden>
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-submissions/:formToken/:uid"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <RedirectIfUiHidden flag="hideSubmissions">
                      <PublicFormSubmissionView />
                    </RedirectIfUiHidden>
                  </PrivateRoute>
                }
              />
              <Route
                path="/info"
                element={
                  <FullScreenComponent title="Info">
                    <InfoPage />
                  </FullScreenComponent>
                }
              />
              <Route path="/etsg-privacy" element={<EtsgPrivacyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route
                path="/profile"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <FullScreenComponent title="Profile">
                      <ProfilePage />
                    </FullScreenComponent>
                  </PrivateRoute>
                }
              />
              <Route path="/registration" element={<RegisterFlowContainer />} />
              <Route path="/register" element={<RegisterFlowContainer />} />
              <Route path="/register/:tenantAbbreviation" element={<RegisterFlowContainer />} />
              <Route path="/register/complete" element={<RegisterComplete />} />
              <Route
                path="/components"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <FullScreenComponent title="Collections">
                      <CollectionsPage />
                    </FullScreenComponent>
                  </PrivateRoute>
                }
              />
              <Route
                path="/collections"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <FullScreenComponent title="Collections">
                      <CollectionsPage />
                    </FullScreenComponent>
                  </PrivateRoute>
                }
              />
              <Route
                path="/files"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <FullScreenComponent title="Files">
                      <FilesPage />
                    </FullScreenComponent>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/collections"
                element={
                  <PrivateRoute>
                    <AdminComponentsGuard>
                      <FullScreenComponent>
                        <Components />
                      </FullScreenComponent>
                    </AdminComponentsGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/connectors"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Connections">
                        <ConnectorsPage />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/connectors/:key/files"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Connector files">
                        <ConnectorFilesPage />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent
                      >
                        <PermissionManager />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/forms"
                element={
                  <PrivateRoute>
                    <FormConfigGuard>
                      <FullScreenComponent>
                        <PublicFormsManager />
                      </FullScreenComponent>
                    </FormConfigGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/json-feeds"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent>
                        <PublicJsonFeedsManager />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/email"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Email">
                        <EmailAdminHub />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/email/logs"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Email logs">
                        <EmailLogsPanel />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/email/provider"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Email provider">
                        <EmailProviderPanel />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/email/quota"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Email quota">
                        <EmailQuotaPanel />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/email/test"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Test mail">
                        <EmailTestPanel />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route path="/admin/logs" element={<Navigate to="/admin/email/logs" replace />} />
              <Route path="/admin/email-test" element={<Navigate to="/admin/email/test" replace />} />
              <Route
                path="/admin/mongo"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Mongo (raw)">
                        <MongoCollectionsInspector />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/system"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="System">
                        <AdminDashboard />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent>
                        <AdminUsersPage />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/groups"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent>
                        <AdminGroupsPage />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/tenants"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <FullScreenComponent title="Tenants">
                        <TenantsPage />
                      </FullScreenComponent>
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
              <Route
                path="/actions"
                element={
                  <PrivateRoute>
                    <RedirectIfUiHidden flag="hideActions">
                      <FullScreenComponent>
                        <Actions />
                      </FullScreenComponent>
                    </RedirectIfUiHidden>
                  </PrivateRoute>
                }
              />
              <Route
                path="/inbox"
                element={
                  <PrivateRoute requireAdmin={false}>
                    <RedirectIfUiHidden flag="hideInbox">
                      <FullScreenComponent title="Inbox">
                        <InboxPage />
                      </FullScreenComponent>
                    </RedirectIfUiHidden>
                  </PrivateRoute>
                }
              />
              <Route
                path="/state"
                element={
                  <StatePage />
                } />
              <Route path="/component/:slug" element={<LegacyCollectionPathRedirect />} />
              <Route
                path="/:org/:slug"
                element={<OrgPrefixedCollectionRedirect />}
              />
              <Route
                path="/:slug"
                element={
                  <PrivateRoute requireAdmin={false} logClient404WhenUnauthenticated>
                    <FullScreenComponent>
                      <ComponentTable />
                    </FullScreenComponent>
                  </PrivateRoute>
                }
              />
              <Route
                path="*"
                element={
                  <FullScreenComponent title="Not found">
                    <NotFoundPage kind="unmatched_route" embedded />
                  </FullScreenComponent>
                }
              />
            </Routes>
          </Router>
        </ACLProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;